import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  calculateDORIZones,
  effectiveHorizontalResolution,
  effectiveSensorSize,
  DORI_COLORS,
} from '@/components/cctv-designer-v2/doriUtils';

/**
 * Top-down coverage plot for the camera SEO pages.
 *
 * Replaces the old fan sketch whose scale was pinned to the effective range:
 * a 40 m IR arc on a 12 m cone rendered 1.5x past the canvas and left broken
 * dashed slivers in the corners. This one derives ONE scene scale that fits
 * every drawn radius, and paints the DORI tier bands with the SAME
 * calculateDORIZones engine the designer, PDF and 3D use — the simulator's
 * numbers and the marketing page's picture can never disagree.
 */
interface FovVisualizationProps {
  hfov: number;
  effRange: number;
  irRange: number;
  resolution?: string;
}

const TIERS = ['identify', 'recognize', 'observe', 'detect'] as const;

const FovVisualization: React.FC<FovVisualizationProps> = ({ hfov, effRange, irRange, resolution }) => {
  const { t } = useTranslation();

  const width = effectiveHorizontalResolution(resolution, undefined, false);
  const sensor = effectiveSensorSize(undefined, resolution);
  const zones = calculateDORIZones(hfov, width, sensor);
  const zoneByType = Object.fromEntries(zones.map((z) => [z.type, z.maxDistance])) as Record<
    (typeof TIERS)[number], number | undefined
  >;

  // One scale for EVERYTHING that gets drawn — nothing may leave the canvas.
  const detectM = zoneByType.detect ?? 0;
  const maxM = Math.max(effRange, irRange, detectM, 5);

  const W = 640;
  const H = 380;
  const cx = W / 2;
  const cy = H - 42;
  const R = H - 108; // px radius available for maxM
  const px = (m: number) => (m / maxM) * R;

  // Panoramic bodies: clamp the DRAWN wedge so the geometry stays readable;
  // the printed hfov stays the real number.
  const drawDeg = Math.min(hfov, 178);
  const half = (drawDeg * Math.PI) / 360;
  const largeArc = drawDeg > 180 ? 1 : 0;

  const pt = (rM: number, sign: -1 | 1) => ({
    x: cx + sign * px(rM) * Math.sin(half),
    y: cy - px(rM) * Math.cos(half),
  });
  /** Filled ring segment of the wedge between rInner..rOuter metres. */
  const band = (rInnerM: number, rOuterM: number) => {
    const ri = px(rInnerM);
    const ro = px(rOuterM);
    const a = pt(rInnerM, -1);
    const b = pt(rOuterM, -1);
    const c = pt(rOuterM, 1);
    const d = pt(rInnerM, 1);
    return `M${a.x},${a.y} L${b.x},${b.y} A${ro},${ro} 0 ${largeArc},1 ${c.x},${c.y} L${d.x},${d.y} A${ri},${ri} 0 ${largeArc},0 ${a.x},${a.y} Z`;
  };
  /** Dashed arc across the wedge at radius rM. */
  const arc = (rM: number) => {
    const a = pt(rM, -1);
    const b = pt(rM, 1);
    return `M${a.x},${a.y} A${px(rM)},${px(rM)} 0 ${largeArc},1 ${b.x},${b.y}`;
  };

  // Ring grid: a step that yields 3-6 rings for any maxM.
  const step = [2, 5, 10, 20, 25, 50, 100, 200, 500, 1000].find((s) => maxM / s <= 6) ?? 1000;
  const rings: number[] = [];
  for (let m = step; m < maxM * 0.999; m += step) rings.push(m);

  // Tier bands from the apex outward: identify is the innermost (highest
  // pixel density), detect the outermost — same semantics as the designer.
  const bands: Array<{ from: number; to: number; color: string; tier: (typeof TIERS)[number] }> = [];
  let prev = 0;
  for (const tier of TIERS) {
    const to = zoneByType[tier];
    if (typeof to !== 'number' || to <= prev) continue;
    bands.push({ from: prev, to: Math.min(to, maxM), color: DORI_COLORS[tier], tier });
    prev = to;
  }

  const tierLabel = (tier: (typeof TIERS)[number]) =>
    tier === 'identify' ? t('cam_tier_identify', { defaultValue: 'Identify' }) :
    tier === 'recognize' ? t('cam_tier_recognize', { defaultValue: 'Recognize' }) :
    tier === 'observe' ? t('cam_tier_observe', { defaultValue: 'Observe' }) :
    t('cam_tier_detect', { defaultValue: 'Detect' });

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-gray-border)] rounded-none p-4 sm:p-8">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
        aria-label={t('cam_fov_viz', { defaultValue: 'Field of View Visualization' })}>
        <defs>
          <radialGradient id="fovGlow" cx="50%" cy="100%" r="90%">
            <stop offset="0%" stopColor="#5B8CFF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#5B8CFF" stopOpacity="0.02" />
          </radialGradient>
        </defs>

        {/* Range ring grid with distance labels along the centre axis */}
        {rings.map((m) => (
          <g key={m}>
            <circle cx={cx} cy={cy} r={px(m)} fill="none" stroke="#334155" strokeWidth="0.6" strokeDasharray="2,4" />
            <text x={cx + 4} y={cy - px(m) - 3} fill="#64748b" fontSize="9">{m} m</text>
          </g>
        ))}

        {/* Wedge ground glow */}
        <path d={band(0, maxM)} fill="url(#fovGlow)" stroke="none" />

        {/* DORI tier bands — engine-identical distances, in-app colors */}
        {bands.map((b) => (
          <path key={b.tier} d={band(b.from, b.to)} fill={b.color} fillOpacity="0.22"
            stroke={b.color} strokeOpacity="0.55" strokeWidth="1" />
        ))}

        {/* Effective range arc */}
        {effRange > 0 && (
          <>
            <path d={arc(effRange)} fill="none" stroke="#5B8CFF" strokeWidth="2" />
            <text x={pt(effRange, 1).x + 6} y={pt(effRange, 1).y + 3} fill="#5B8CFF" fontSize="10" fontWeight="600">
              {effRange} m
            </text>
          </>
        )}

        {/* IR range arc — inside the canvas by construction of the scale */}
        {irRange > 0 && (
          <>
            <path d={arc(irRange)} fill="none" stroke="#f87171" strokeWidth="1.4" strokeDasharray="5,4" opacity="0.85" />
            <text x={pt(irRange, -1).x - 6} y={pt(irRange, -1).y + 3} fill="#f87171" fontSize="10" textAnchor="end">
              IR {irRange} m
            </text>
          </>
        )}

        {/* Wedge outline on top */}
        <path d={`M${cx},${cy} L${pt(maxM, -1).x},${pt(maxM, -1).y} A${px(maxM)},${px(maxM)} 0 ${largeArc},1 ${pt(maxM, 1).x},${pt(maxM, 1).y} Z`}
          fill="none" stroke="#5B8CFF" strokeOpacity="0.6" strokeWidth="1" />

        {/* Camera glyph at the apex */}
        <circle cx={cx} cy={cy} r="7" fill="#0f172a" stroke="#5B8CFF" strokeWidth="2" />
        <circle cx={cx} cy={cy} r="2.5" fill="#5B8CFF" />
        <text x={cx} y={cy + 22} textAnchor="middle" fill="#94a3b8" fontSize="10">
          {t('cam_svg_camera', { defaultValue: 'Camera' })} · {t('cam_svg_fov_label', { defaultValue: '{{hfov}}° FOV', hfov })}
        </text>
      </svg>

      {/* Tier legend: colored chips with the engine's distances */}
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 justify-center text-xs">
        {[...bands].reverse().map((b) => (
          <span key={b.tier} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.color }} />
            <span className="text-[var(--color-gray-muted)]">{tierLabel(b.tier)}</span>
            <span className="font-mono tabular-nums text-foreground">{Math.round(b.to)} m</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-0.5" style={{ backgroundColor: '#5B8CFF' }} />
          <span className="text-[var(--color-gray-muted)]">{t('cam_svg_effective_range', { defaultValue: 'Effective range' })}</span>
        </span>
        {irRange > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: '#f87171' }} />
            <span className="text-[var(--color-gray-muted)]">{t('cam_svg_ir_range', { defaultValue: 'IR range ({{irRange}}m)', irRange })}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default FovVisualization;
